import { SendOutlined } from "@ant-design/icons";
import { Avatar, Button, Mentions, Select, Spin, Switch } from "antd";
import { BusinessMessage } from "components/messenger/BusinessMessage";
import { FoodieMessage } from "components/messenger/FoodieMessage";
import { MustardMessage } from "components/messenger/MustardMessage";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  assignChatToUsers,
  deleteSuperMessengerMessage,
  getSuperMessengerChat,
  markSuperMessengerChatAsRead,
  markSuperMessengerChatAsReplied,
  markSuperMessengerChatAsUnreplied,
  sendSuperMessengerMessage,
} from "services/SauceService";
import { UserState } from "redux-context/user";
import { useSelector } from "react-redux";
import { Chat as ChatType } from "enums/ChatType";
import "./Chat.scss";
import { ReactComponent as LocationIcon } from "assets/images/location-icon.svg";
import { isMobile } from "react-device-detect";
import { DomainState } from "redux-context/domain";
import { sseService } from "services/SSEStream";
import { formatBookingDate } from "helpers/messenger";
import { ActionMessage } from "components/messenger/ActionMessage";
import { faUserPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export const getParticipantsByType = (chat: ChatType) => {
  const foodie = chat.participants.find(
    (participant) => participant.participantType === "foodie"
  );
  const business = chat.participants.find(
    (participant) => participant.participantType === "business"
  );

  return {
    foodie,
    business,
  };
};

const createDummyMessage = ({
  content,
  loggedUser,
  senderType,
}: {
  content: string;
  loggedUser: any;
  senderType: "business" | "mustard" | "mustard-on-behalf-of-business";
}) => {
  return {
    content,
    messageType: "text",
    state: "sending",
    senderType,
    sender: {
      _id: loggedUser._id,
      name: senderType === "mustard" ? "Mustard" : loggedUser.name,
      avatarUrl:
        senderType === "mustard"
          ? "https://app-collabs.mustard.love/logo192.png"
          : loggedUser.avatarUrl,
    },
    _id: `pending-${Date.now().toString()}`,
    createdAt: new Date().toISOString(),
  };
};

export default function Chat({
  id,
  onMessageSent,
}: {
  id: string;
  onMessageSent: () => void;
}) {
  // const [businessId, setBusinessId] = useState<string | null>();
  const { metadata } = useSelector((state: DomainState) => state.domain);
  const [chat, setChat] = useState<ChatType | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const { loggedUser } = useSelector((state: UserState) => state.user);
  const isAdminUser = loggedUser?.email?.endsWith(`@${metadata?.domain}`);
  const [senderType, setSenderType] = useState<
    "mustard" | "mustard-on-behalf-of-business" | "business"
  >();
  const [chatAssignee, setChatAssignee] = useState<string[]>([]);
  const [chatReplied, setChatReplied] = useState(false);

  const newMessageSubscribeRef = useRef<() => void>();
  const deleteMessageSubscribeRef = useRef<() => void>();

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop =
          messagesContainerRef.current.scrollHeight;
      }
    }, 0);
  }, [messagesContainerRef]);

  useEffect(() => {
    if (!id) return;
    scrollToBottom();
  }, [id, scrollToBottom]);

  const markAsRead = useCallback(async () => {
    if (!id) return;
    try {
      console.log("marking chat as read");
      await markSuperMessengerChatAsRead(id);
    } catch (error) {
      //ignore
    }
  }, [id]);

  useEffect(() => {
    newMessageSubscribeRef.current = sseService.subscribe(
      "superchat/new-message",
      (event) => {
        console.log("superchat/new-message");
        const message = JSON.parse(event.data) as ChatType["messages"][0];
        if (message.chatId !== id) {
          console.log("skipping message");
          return;
        }

        setChat((prevChat) => {
          if (!prevChat) return prevChat;

          const duplicateMessage = prevChat.messages.find(
            (msg) => msg._id === message._id
          );

          if (duplicateMessage) {
            console.log("duplicate message");
            return prevChat;
          }

          const messagesWithoutPendingAndDuplicate = prevChat.messages.filter(
            (msg) => !msg._id.startsWith("pending-") && msg._id !== message._id
          );

          return {
            ...prevChat,
            messages: [...messagesWithoutPendingAndDuplicate, message],
          };
        });

        scrollToBottom();
        markAsRead();
      }
    );

    deleteMessageSubscribeRef.current = sseService.subscribe(
      "superchat/delete-message",
      (event) => {
        console.log("superchat/delete-message");
        const data = JSON.parse(event.data) as {
          chatId: string;
          messageId: string;
        };

        if (data.chatId !== id) {
          console.log("skipping message");
          return;
        }

        setChat((prevChat) => {
          if (!prevChat) return prevChat;
          return {
            ...prevChat,
            messages: prevChat.messages.filter(
              (msg) => msg._id !== data.messageId
            ),
          };
        });
      }
    );

    return () => {
      newMessageSubscribeRef.current?.();
      deleteMessageSubscribeRef.current?.();
    };
  }, [id, markAsRead, scrollToBottom]);

  useEffect(() => {
    if (isAdminUser === undefined || senderType) return;

    if (isAdminUser) {
      setSenderType("mustard-on-behalf-of-business");
    } else {
      setSenderType("business");
    }
  }, [isAdminUser, senderType]);

  const businessParticipant = chat?.participants.find(
    (participant) => participant.participantType === "business"
  );

  useEffect(() => {
    const abortController = new AbortController();
    if (id) {
      setLoading(true);
      getSuperMessengerChat(id, abortController)
        .then((res) => {
          //@ts-ignore
          setChat(res);
          //@ts-ignore
          setChatAssignee(res.chatAssignee ?? []);
          //@ts-ignore
          setChatReplied(res.replied ?? false);
          //@ts-ignore
          if (res.context.type === "foodie-mustard") {
            setSenderType("mustard");
            //@ts-ignore
          } else if (res.context.type === "business-mustard") {
            setSenderType("mustard");
          }
        })
        .then(() => {
          scrollToBottom();
          markAsRead();
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          setLoading(false);
        });
    }

    return () => {
      abortController.abort();
    };
  }, [id, markAsRead, scrollToBottom]);

  const handleSendMessage = async () => {
    if (!chat || !message || isSendingMessage || message.trim().length === 0)
      return;

    const originalMessage = message;
    setMessage("");

    try {
      setIsSendingMessage(true);
      setChatReplied(true);

      setChat((prevChat) => {
        if (!prevChat) return prevChat;

        return {
          ...prevChat,
          messages: [
            ...prevChat.messages,
            createDummyMessage({
              content: originalMessage,
              loggedUser:
                senderType === "mustard-on-behalf-of-business"
                  ? businessParticipant
                  : loggedUser,
              senderType: senderType ?? "business",
            }) as ChatType["messages"][number],
          ],
        };
      });

      scrollToBottom();

      if (!senderType) {
        return;
      }

      //@ts-ignore
      const messageFromServer: ChatType["messages"][number] =
        await sendSuperMessengerMessage(chat._id, {
          message: originalMessage,
          senderType,
          sauceUserId: loggedUser?.id,
          businessId: businessParticipant?.participantId,
        });

      setChat((prevChat) => {
        if (!prevChat) return prevChat;

        const messagesWithoutPendingAndDuplicate: ChatType["messages"] =
          prevChat?.messages.filter(
            (msg) =>
              !msg._id.startsWith("pending-") &&
              msg._id !== messageFromServer._id
          ) ?? [];

        return {
          ...prevChat,
          messages: [...messagesWithoutPendingAndDuplicate, messageFromServer],
        };
      });

      onMessageSent();
      scrollToBottom();
    } catch (error) {
      console.error(error);
      setChat({
        ...chat,
        messages: [
          ...chat.messages,
          {
            ...(createDummyMessage({
              content: originalMessage,
              loggedUser:
                senderType === "mustard-on-behalf-of-business"
                  ? businessParticipant
                  : loggedUser,
              senderType: senderType ?? "business",
            }) as ChatType["messages"][number]),
            state: "error",
          },
        ],
      });
    } finally {
      setIsSendingMessage(false);
    }
  };

  if (!chat || loading) {
    return (
      <div className={`chat ${isMobile ? "--mobile" : ""} --loading`}>
        <Spin />
      </div>
    );
  }

  const chatType = chat.context.type;
  const { foodie, business } = getParticipantsByType(chat);

  const handleDeleteMessage = async (message: ChatType["messages"][number]) => {
    try {
      await deleteSuperMessengerMessage(chat._id, message._id);
      setChat((prevChat) => {
        if (!prevChat) return prevChat;
        return {
          ...prevChat,
          messages: prevChat.messages.filter((msg) => msg._id !== message._id),
        };
      });
    } catch (error) {
      setChat((prevChat) => {
        if (!prevChat) return prevChat;
        const messageIndex = prevChat.messages.findIndex(
          (msg) => msg._id === message._id
        );
        const messageToUpdate = prevChat.messages[messageIndex];

        const newMessage = {
          ...messageToUpdate,
          canDelete: false,
        } as ChatType["messages"][number];

        prevChat.messages[messageIndex] = newMessage;

        return {
          ...prevChat,
          messages: prevChat.messages,
        };
      });
      window.alert("Failed to delete message because it was already read");
    }
  };

  return (
    <div className={`supermessenger-chat ${isMobile ? "--mobile" : ""}`}>
      <div className="supermessenger-chat__card">
        <div className="supermessenger-chat__header">
          <div className="supermessenger-chat__header-content">
            <div className="supermessenger-chat__header-avatars">
              {chatType === "collab" && (
                <>
                  <Avatar src={foodie?.avatarUrl} size={isMobile ? 32 : 40} />
                  <Avatar
                    src={business?.avatarUrl}
                    size={isMobile ? 32 : 40}
                    className="supermessenger-chat__header-avatar-overlap"
                  />
                </>
              )}
              {chatType === "foodie-mustard" && (
                <div className="mustard">
                  <Avatar src={foodie?.avatarUrl} size={isMobile ? 32 : 40} />
                  <Avatar
                    src={metadata?.smallLogoUrl}
                    size={isMobile ? 32 : 40}
                    className="supermessenger-chat__header-avatar-overlap"
                  />
                </div>
              )}
              {chatType === "business-mustard" && (
                <div className="mustard">
                  <Avatar
                    src={businessParticipant?.avatarUrl}
                    size={isMobile ? 32 : 40}
                  />
                  <Avatar
                    src={metadata?.smallLogoUrl}
                    size={isMobile ? 32 : 40}
                    className="supermessenger-chat__header-avatar-overlap"
                  />
                </div>
              )}
            </div>
            <div>
              <div className="supermessenger-chat__header-names">
                <div>
                  <span className="supermessenger-chat__header-name">
                    {chatType === "collab" && (
                      <>
                        {foodie?.participantName}{" "}
                        <span className="supermessenger-chat__header-name-separator">
                          and
                        </span>{" "}
                        {business?.participantName}
                      </>
                    )}
                    {chatType === "foodie-mustard" && (
                      <>
                        {foodie?.participantName}{" "}
                        <span className="supermessenger-chat__header-name-separator">
                          and
                        </span>{" "}
                        Mustard
                      </>
                    )}
                    {chatType === "business-mustard" && (
                      <>
                        Mustard support for{" "}
                        {businessParticipant?.participantName}{" "}
                      </>
                    )}
                  </span>
                </div>
                <div>
                  {chat.context.booking?.bookDate ? (
                    <>· {formatBookingDate(chat.context.booking.bookDate)}</>
                  ) : (
                    ""
                  )}
                </div>
                <div>
                  {chat.context.booking?.bookHour ? (
                    <>· {chat.context.booking.bookHour}</>
                  ) : (
                    ""
                  )}
                </div>
              </div>
              <div className="supermessenger-chat__header__context-info">
                <span className="supermessenger-chat__header__context-item">
                  {chatType === "collab" && (
                    <>
                      <LocationIcon className="icon" />
                      <span>{chat.context.venue?.address ?? "-"}</span>
                    </>
                  )}
                </span>
              </div>
            </div>
          </div>
          <div className="supermessenger-chat__header-actions">
            <div>
              <Select
                mode="multiple"
                className="supermessenger-chat__header-assign-select"
                suffixIcon={<FontAwesomeIcon icon={faUserPlus} />}
                maxTagCount={2}
                placeholder="Assign to"
                value={chatAssignee}
                onChange={(value) => {
                  setChatAssignee(value);
                  assignChatToUsers(id, value);
                }}
                options={[
                  {
                    value: "alice",
                    label: (
                      <span className="supermessenger-chat__header-assign-option">
                        Alice
                      </span>
                    ),
                  },
                  {
                    value: "bella",
                    label: (
                      <span className="supermessenger-chat__header-assign-option">
                        Bella
                      </span>
                    ),
                  },
                  {
                    value: "diana",
                    label: (
                      <span className="supermessenger-chat__header-assign-option">
                        Diana
                      </span>
                    ),
                  },
                  {
                    value: "ana",
                    label: (
                      <span className="supermessenger-chat__header-assign-option">
                        Ana
                      </span>
                    ),
                  },
                  {
                    value: "lu",
                    label: (
                      <span className="supermessenger-chat__header-assign-option">
                        Lu
                      </span>
                    ),
                  },
                ]}
              />
            </div>
            <div className="supermessenger-chat__header-replied-container">
              <span className="supermessenger-chat__header-replied-label">
                Replied
              </span>
              <Switch
                checked={chatReplied}
                onChange={async (value) => {
                  setChatReplied(value);
                  if (value) {
                    await markSuperMessengerChatAsReplied(id);
                  } else {
                    await markSuperMessengerChatAsUnreplied(id);
                  }

                  onMessageSent();
                }}
                className="supermessenger-chat__header-replied-switch"
              />
            </div>
          </div>
        </div>
        <div
          className="supermessenger-chat__messages"
          ref={messagesContainerRef}
        >
          <div className="supermessenger-chat__messages-container">
            <div>
              {chat.messages.map((message) => (
                <React.Fragment key={message._id}>
                  {message.senderType === "foodie" && (
                    <FoodieMessage key={message._id} {...message} />
                  )}
                  {message.senderType === "business" && (
                    <BusinessMessage
                      key={message._id}
                      {...message}
                      alignment={
                        chat.context.type === "business-mustard"
                          ? "left"
                          : "right"
                      }
                    />
                  )}
                  {message.senderType === "mustard-on-behalf-of-business" && (
                    <BusinessMessage
                      key={message._id}
                      {...message}
                      alignment={
                        chat.context.type === "business-mustard"
                          ? "left"
                          : "right"
                      }
                      onDeleteClick={() => handleDeleteMessage(message)}
                    />
                  )}
                  {message.senderType === "mustard" &&
                    message.messageType === "text" && (
                      <MustardMessage
                        key={message._id}
                        {...message}
                        alignment={
                          chat.context.type === "foodie-mustard" ||
                            chat.context.type === "business-mustard"
                            ? "right"
                            : "left"
                        }
                        onDeleteClick={() => handleDeleteMessage(message)}
                      />
                    )}
                  {message.senderType === "mustard" &&
                    message.messageType === "business-action" && (
                      <ActionMessage
                        key={message._id}
                        {...message}
                        alignment="right"
                      />
                    )}
                  {message.senderType === "mustard" &&
                    message.messageType === "foodie-action" && (
                      <ActionMessage
                        key={message._id}
                        {...message}
                        alignment="right"
                      />
                    )}
                  {/* {message.senderType === "mustard" &&
                    message.messageType === "suggestion-ai" && (
                      <AISuggestionMessage
                        key={message._id}
                        {...message}
                        chatId={chat._id}
                        onApproveSuccess={async () => {
                          try {
                            const updatedChat = await getSuperMessengerChat(id);
                            //@ts-ignore
                            setChat(updatedChat);
                            scrollToBottom();
                            onMessageSent();
                          } catch (error) {
                            console.error("Error refreshing chat:", error);
                            onMessageSent();
                          }
                        }}
                      />
                    )} */}
                  {![
                    "foodie",
                    "business",
                    "mustard",
                    "mustard-on-behalf-of-business",
                  ].includes(message.senderType) && (
                      <>
                        <div>Type: {message.senderType}</div>
                        <div className="card">{message.content}</div>
                      </>
                    )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
        <div className="supermessenger-chat__input-container">
          <Mentions
            value={message}
            onChange={(value) => setMessage(value)}
            placeholder="Type your message here..."
            className="supermessenger-chat__input-textarea"
            onPressEnter={handleSendMessage}
            placement="top"
            options={[
              { value: "mustard", label: "@Mustard", style: { color: "blue" } },
            ]}
          />
          <div className="supermessenger-chat__input-actions">
            <div className="supermessenger-chat__input-buttons">
              <Button
                type="primary"
                className="supermessenger-chat__input-send"
                disabled={isSendingMessage}
                onClick={handleSendMessage}
              >
                <SendOutlined className="supermessenger-chat__input-send-icon" />
              </Button>
              {isAdminUser && (
                <Select
                  labelInValue={false}
                  suffixIcon={null}
                  className="supermessenger-chat__input-select"
                  dropdownClassName="supermessenger-chat__input-select-dropdown"
                  value={senderType}
                  onChange={(value) => setSenderType(value)}
                  disabled={
                    chatType === "business-mustard" ||
                    chatType === "foodie-mustard"
                  }
                  options={
                    chatType === "collab"
                      ? [
                        {
                          label: (
                            <Avatar src={businessParticipant?.avatarUrl} />
                          ),
                          value: "mustard-on-behalf-of-business",
                        },
                        {
                          value: "mustard",
                          label: (
                            <div className="avatar">
                              <Avatar src={metadata?.smallLogoUrl} />
                            </div>
                          ),
                        },
                      ]
                      : [
                        {
                          value: "mustard",
                          label: (
                            <div className="avatar">
                              <Avatar src={metadata?.smallLogoUrl} />
                            </div>
                          ),
                        },
                      ]
                  }
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
